import React, { useState, useEffect } from "react";
import { Modal, Tag, Button, Select, Typography, Space, message, Popconfirm } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../slice";
import * as selectors from "../selectors";

const { Text } = Typography;

const ManageBoxOrders = ({
  visible,
  onClose,
  boxData,
  fetchKitDetail,
  setBoxData
}) => {
  const dispatch = useDispatch();
  const eligibleOrders = useSelector(selectors.selectEligibleOrders);
  
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingOrderId, setRemovingOrderId] = useState(null);

  const MAX_ORDERS = 4;
  const currentOrderCount = boxData?.orderIds?.length || 0;

  useEffect(() => {
    if (visible && boxData) {
      setSelectedOrders([]);
      setRemovingOrderId(null);
      
      // Fetch eligible orders for this box
      dispatch(
        actions.fetchEligibleOrders({
          samplerBoxId: boxData._id,
        })
      );
    }
  }, [visible, boxData, dispatch]);

  const refreshBoxData = () => {
    // Refresh eligible orders after kit detail update
    if (boxData?._id) {
      dispatch(
        actions.fetchEligibleOrders({
          samplerBoxId: boxData._id,
        })
      );
    }
  };

  const handleAddOrders = () => {
    if (selectedOrders.length === 0) {
      message.warning("Please select at least one order");
      return;
    }
    setLoading(true);
    
    dispatch(
      actions.addOrdersToBox({
        postData: {
          samplerBoxId: boxData._id,
          orderIds: selectedOrders,
        },
        onSuccess: (data) => {
          const addedCount = data?.data?.addedOrders?.length || 0;
          message.success(`${addedCount} order(s) added successfully`);
          setLoading(false);
          setBoxData(prev => ({
            ...prev,
            orderIds: [...prev.orderIds, ...data?.data?.addedOrders.map(order => ({orderId: order.orderId, _id: order.orderId}))]
          }));
          setSelectedOrders([]);
          refreshBoxData();
          fetchKitDetail(false);
        },
        onFailure: (error) => {
          message.error(error?.message || "Failed to add orders to box");
          setLoading(false);
        },
      })
    );
  };

  const handleRemoveOrder = (orderId) => {
    setRemovingOrderId(orderId);
    
    dispatch(
      actions.removeOrderFromBox({
        postData: {
          samplerBoxId: boxData._id,
          orderId: orderId,
        },
        onSuccess: (data) => {
          message.success(data?.message || "Order removed successfully");
          setRemovingOrderId(null);
          fetchKitDetail(false);
          setBoxData(prev => ({
            ...prev,
            orderIds: prev.orderIds.filter(order => order.orderId !== orderId)
          }));
          // Refresh modal data after parent updates
          refreshBoxData();
        },
        onFailure: (error) => {
          message.error(error?.message || "Failed to remove order from box");
          setRemovingOrderId(null);
        },
      })
    );
  };

  const isMaxReached = currentOrderCount >= MAX_ORDERS;
  const availableSlots = MAX_ORDERS - currentOrderCount;

  const eligibleOrderOptions = eligibleOrders?.map((order) => ({
    label: order.orderId,
    value: order.orderId,
  }));

  return (
    <Modal
      title={`Manage Orders for Box: ${boxData?.boxId || ""}`}
      visible={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="add"
          type="primary"
          onClick={handleAddOrders}
          disabled={selectedOrders.length === 0 || isMaxReached}
          loading={loading}
          style={{ background: selectedOrders.length > 0 ? "#23235F" : undefined }}
        >
          Add Orders
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Currently Associated Orders */}
        <div>
          <Text strong style={{ fontSize: 16, marginBottom: 12, display: "block" }}>
            Currently Associated Orders ({currentOrderCount}/{MAX_ORDERS})
          </Text>
          
          {currentOrderCount === 0 ? (
            <Text type="secondary">No orders associated with this box yet.</Text>
          ) : (
            <Space wrap style={{ marginTop: 8 }}>
              {boxData?.orderIds?.map((order) => (
                <div key={order._id} style={{ position: 'relative', display: 'inline-block' }}>
                  <Tag
                    style={{
                      padding: "6px 32px 6px 12px",
                      fontSize: 14,
                      borderRadius: 6,
                      background: "#E5E8EC",
                      border: "1px solid #d9d9d9",
                      opacity: removingOrderId === order.orderId ? 0.6 : 1,
                      cursor: 'default',
                    }}
                  >
                    {order.orderId}
                  </Tag>
                  <Popconfirm
                    title="Remove Order"
                    description={`Are you sure you want to remove order ${order.orderId}?`}
                    onConfirm={() => handleRemoveOrder(order.orderId)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                    disabled={removingOrderId === order.orderId}
                  >
                    <CloseOutlined 
                      style={{ 
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: removingOrderId === order.orderId ? '#999' : '#666',
                        cursor: removingOrderId === order.orderId ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        padding: '4px',
                      }} 
                    />
                  </Popconfirm>
                </div>
              ))}
            </Space>
          )}

          {isMaxReached && (
            <div
              style={{
                marginTop: 12,
                padding: "8px 12px",
                background: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: 6,
              }}
            >
              <Text type="warning" style={{ fontSize: 13 }}>
                ⚠️ Maximum limit of {MAX_ORDERS} orders reached for this box.
              </Text>
            </div>
          )}
        </div>

        {/* Add Orders Section */}
        {!isMaxReached && (
          <div>
            <Text strong style={{ fontSize: 14, marginBottom: 8, display: "block" }}>
              Add Orders ({availableSlots} slot{availableSlots !== 1 ? "s" : ""} available)
            </Text>
            <Select
              mode="multiple"
              placeholder="Search and select orders..."
              style={{ width: "100%" }}
              value={selectedOrders}
              onChange={setSelectedOrders}
              options={eligibleOrderOptions}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              disabled={isMaxReached}
              notFoundContent={
                <Text type="secondary" style={{ padding: 8, display: "block" }}>
                  No eligible orders available.
                </Text>
              }
            />
            {selectedOrders.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedOrders.length} order{selectedOrders.length !== 1 ? "s" : ""} selected
                  {currentOrderCount + selectedOrders.length > MAX_ORDERS && (
                    <span style={{ color: "#ff4d4f", marginLeft: 8 }}>
                      (Exceeds maximum capacity)
                    </span>
                  )}
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Info Message */}
        <div
          style={{
            padding: "12px",
            background: "#f0f5ff",
            border: "1px solid #adc6ff",
            borderRadius: 6,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            ℹ️ Only valid orders can be associated with this box. Maximum {MAX_ORDERS} orders per box. Click the × icon to remove an order.
          </Text>
        </div>
      </Space>
    </Modal>
  );
};

export default ManageBoxOrders;